import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import FileUpload from '../../components/common/FileUpload';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Shield, User, Briefcase, GraduationCap, Check, ChevronRight, ChevronLeft, 
  MapPin, Linkedin, X, Plus, Loader2, Eye, FileText
} from 'lucide-react';

const INDUSTRIES = [
  "Technology", "Finance", "Healthcare", "Education", "Retail",
  "Manufacturing", "Consulting", "Marketing", "Legal", "Real Estate",
  "Hospitality", "Transportation", "Energy", "Media", "Non-profit"
];

const YEARS_EXPERIENCE = [
  { value: "0-1", label: "0-1 years" },
  { value: "1-3", label: "1-3 years" },
  { value: "3-5", label: "3-5 years" },
  { value: "5-10", label: "5-10 years" },
  { value: "10+", label: "10+ years" }
];

const EDUCATION_LEVELS = [
  "High School",
  "Associate's",
  "Bachelor's",
  "Master's",
  "PhD",
  "Bootcamp/Certification",
  "Other"
];

const WORK_LOCATIONS = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" }
];

const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "freelance", label: "Freelance" }
];

const EmployeeOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  
  // Step 1: Personal Details
  const [photoUrl, setPhotoUrl] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  
  // Step 2: Skills & Experience
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [education, setEducation] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  
  // Step 3: Opportunity Preferences
  const [opportunityStatus, setOpportunityStatus] = useState('');
  const [interestedRoles, setInterestedRoles] = useState('');
  const [preferredLocations, setPreferredLocations] = useState([]);
  const [preferredEmployment, setPreferredEmployment] = useState([]);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const res = await employeeAPI.getProfile();
      if (res.data.onboarding_complete) {
        navigate('/employee/dashboard', { replace: true });
        return;
      }
      // Pre-fill any existing data
      if (res.data.photo_url) setPhotoUrl(res.data.photo_url);
      if (res.data.first_name) setFirstName(res.data.first_name);
      if (res.data.last_name) setLastName(res.data.last_name);
      if (res.data.job_title) setJobTitle(res.data.job_title);
      if (res.data.industry) setIndustry(res.data.industry);
      if (res.data.location) setLocation(res.data.location);
      if (res.data.bio) setBio(res.data.bio);
      if (res.data.skills) setSkills(res.data.skills);
      if (res.data.open_to_opportunities) setOpportunityStatus(res.data.open_to_opportunities);
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setCheckingOnboarding(false);
    }
  };

  const totalSteps = 4;
  const canSkip = currentStep > 1 && currentStep < 4;

  const handleNext = () => {
    if (currentStep === 1) {
      if (!firstName.trim()) {
        toast.error('First name is required');
        return;
      }
      if (!lastName.trim()) {
        toast.error('Last name is required');
        return;
      }
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (canSkip) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleAddSkill = () => {
    const skill = skillInput.trim();
    if (skill && !skills.includes(skill)) {
      setSkills(prev => [...prev, skill]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setSkills(prev => prev.filter(s => s !== skillToRemove));
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const togglePreferredLocation = (value) => {
    setPreferredLocations(prev => 
      prev.includes(value) ? prev.filter(l => l !== value) : [...prev, value]
    );
  };

  const togglePreferredEmployment = (value) => {
    setPreferredEmployment(prev => 
      prev.includes(value) ? prev.filter(e => e !== value) : [...prev, value]
    );
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await employeeAPI.updateProfile({
        photo_url: photoUrl || null,
        first_name: firstName,
        last_name: lastName,
        job_title: jobTitle || null,
        industry: industry || null,
        location: location || null,
        bio: bio || null,
        skills: skills.length > 0 ? skills : null,
        years_experience: yearsExperience || null,
        education: education || null,
        linkedin_url: linkedinUrl || null,
        open_to_opportunities: opportunityStatus || 'not_looking',
        interested_roles: interestedRoles || null,
        preferred_locations: preferredLocations.length > 0 ? preferredLocations : null,
        preferred_employment: preferredEmployment.length > 0 ? preferredEmployment : null,
        onboarding_complete: true
      });
      
      toast.success('Profile setup complete!');
      navigate('/employee/dashboard');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  if (checkingOnboarding) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-[#1B5AFF]" />
            <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
          </div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">Set up your profile</h1>
          <p className="text-[#5B6472] mt-2">Let's make your profile stand out</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#5B6472] text-sm">Step {currentStep} of {totalSteps}</span>
            {canSkip && (
              <button 
                onClick={handleSkip}
                className="text-[#5B6472] text-sm hover:text-[#0A0F1E] transition-colors"
              >
                Skip this step
              </button>
            )}
          </div>
          <div className="h-2 bg-[#FFFFFF] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#1B5AFF] transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Personal Details */}
        {currentStep === 1 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="employee-onboarding-step-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
                <User className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Personal Details</h2>
                <p className="text-[#5B6472] text-sm">Tell us about yourself</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-[#0A0F1E]">Profile Photo (Optional)</Label>
                <FileUpload
                  value={photoUrl}
                  onChange={setPhotoUrl}
                  placeholder="Upload profile photo"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#0A0F1E]">First Name *</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[#0A0F1E]">Last Name *</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Current or Most Recent Job Title</Label>
                <Input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Software Engineer"
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                />
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Industry</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {INDUSTRIES.map(ind => (
                      <SelectItem key={ind} value={ind} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {ind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Location (City, Country)</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5B6472]" />
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="San Francisco, USA"
                    className="mt-1 pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Short Bio (Optional)</Label>
                <Textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 200))}
                  placeholder="Tell employers a bit about yourself..."
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
                  maxLength={200}
                />
                <div className="text-right text-[#5B6472] text-xs mt-1">{bio.length}/200</div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Skills & Experience */}
        {currentStep === 2 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="employee-onboarding-step-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Skills & Experience</h2>
                <p className="text-[#5B6472] text-sm">Highlight your expertise</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-[#0A0F1E]">Skills</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleSkillKeyDown}
                    placeholder="Type a skill and press Enter"
                    className="flex-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                  <Button
                    type="button"
                    onClick={handleAddSkill}
                    className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl px-4"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
                {skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {skills.map((skill, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1B5AFF]/10 text-[#1B5AFF] text-sm border border-[#1B5AFF]/30"
                      >
                        {skill}
                        <button
                          onClick={() => handleRemoveSkill(skill)}
                          className="ml-1 hover:text-[#0A0F1E]"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Years of Total Work Experience</Label>
                <Select value={yearsExperience} onValueChange={setYearsExperience}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {YEARS_EXPERIENCE.map(exp => (
                      <SelectItem key={exp.value} value={exp.value} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {exp.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Highest Level of Education</Label>
                <Select value={education} onValueChange={setEducation}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select education level" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {EDUCATION_LEVELS.map(level => (
                      <SelectItem key={level} value={level} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">LinkedIn Profile URL (Optional)</Label>
                <div className="relative">
                  <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5B6472]" />
                  <Input
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                    className="mt-1 pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Opportunity Preferences */}
        {currentStep === 3 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="employee-onboarding-step-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Opportunity Preferences</h2>
                <p className="text-[#5B6472] text-sm">Let employers know you're available</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-[#0A0F1E] mb-3 block">Are you open to new opportunities?</Label>
                <div className="grid gap-3">
                  {/* Open to Opportunities */}
                  <button
                    type="button"
                    onClick={() => setOpportunityStatus('open')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      opportunityStatus === 'open'
                        ? 'border-green-500 bg-green-500/10'
                        : 'border-[#E8EDF5] bg-[#F5F8FF]/30 hover:border-green-500/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${opportunityStatus === 'open' ? 'bg-green-500' : 'bg-[#F5F8FF]'}`} />
                        <div>
                          <div className={`font-medium ${opportunityStatus === 'open' ? 'text-[#0A0F1E]' : 'text-[#5B6472]'}`}>
                            Open to Opportunities
                          </div>
                          <div className="text-xs text-[#5B6472]">Actively looking for new roles</div>
                        </div>
                      </div>
                      {opportunityStatus === 'open' && <Check className="w-5 h-5 text-green-500" />}
                    </div>
                  </button>

                  {/* Casually Looking */}
                  <button
                    type="button"
                    onClick={() => setOpportunityStatus('selective')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      opportunityStatus === 'selective'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-[#E8EDF5] bg-[#F5F8FF]/30 hover:border-amber-500/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${opportunityStatus === 'selective' ? 'bg-amber-500' : 'bg-[#F5F8FF]'}`} />
                        <div>
                          <div className={`font-medium ${opportunityStatus === 'selective' ? 'text-[#0A0F1E]' : 'text-[#5B6472]'}`}>
                            Casually Looking
                          </div>
                          <div className="text-xs text-[#5B6472]">Open to the right opportunity</div>
                        </div>
                      </div>
                      {opportunityStatus === 'selective' && <Check className="w-5 h-5 text-amber-500" />}
                    </div>
                  </button>

                  {/* Not Looking */}
                  <button
                    type="button"
                    onClick={() => setOpportunityStatus('not_looking')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      opportunityStatus === 'not_looking'
                        ? 'border-[#5B6472] bg-[#5B6472]/10'
                        : 'border-[#E8EDF5] bg-[#F5F8FF]/30 hover:border-[#5B6472]/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${opportunityStatus === 'not_looking' ? 'bg-[#5B6472]' : 'bg-[#F5F8FF]'}`} />
                        <div>
                          <div className={`font-medium ${opportunityStatus === 'not_looking' ? 'text-[#0A0F1E]' : 'text-[#5B6472]'}`}>
                            Not Looking
                          </div>
                          <div className="text-xs text-[#5B6472]">Not interested right now</div>
                        </div>
                      </div>
                      {opportunityStatus === 'not_looking' && <Check className="w-5 h-5 text-[#5B6472]" />}
                    </div>
                  </button>
                </div>
                <p className="text-[#5B6472] text-xs mt-3">
                  You can change this at any time from your profile settings.
                </p>
              </div>

              {/* Additional preferences if open or selective */}
              {(opportunityStatus === 'open' || opportunityStatus === 'selective') && (
                <>
                  <div>
                    <Label className="text-[#0A0F1E]">What type of roles are you interested in?</Label>
                    <Textarea
                      value={interestedRoles}
                      onChange={(e) => setInterestedRoles(e.target.value.slice(0, 200))}
                      placeholder="e.g., Senior Software Engineer, Tech Lead, Product Manager..."
                      className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
                      maxLength={200}
                    />
                    <div className="text-right text-[#5B6472] text-xs mt-1">{interestedRoles.length}/200</div>
                  </div>

                  <div>
                    <Label className="text-[#0A0F1E] mb-2 block">Preferred Work Location</Label>
                    <div className="flex flex-wrap gap-2">
                      {WORK_LOCATIONS.map(loc => (
                        <button
                          key={loc.value}
                          type="button"
                          onClick={() => togglePreferredLocation(loc.value)}
                          className={`px-4 py-2 rounded-xl border transition-all ${
                            preferredLocations.includes(loc.value)
                              ? 'border-[#1B5AFF] bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]'
                              : 'border-[#E8EDF5] bg-[#F5F8FF]/30 text-[#5B6472] hover:border-[#1B5AFF]/50'
                          }`}
                        >
                          {loc.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#0A0F1E] mb-2 block">Preferred Employment Type</Label>
                    <div className="flex flex-wrap gap-2">
                      {EMPLOYMENT_TYPES.map(type => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => togglePreferredEmployment(type.value)}
                          className={`px-4 py-2 rounded-xl border transition-all ${
                            preferredEmployment.includes(type.value)
                              ? 'border-[#1B5AFF] bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]'
                              : 'border-[#E8EDF5] bg-[#F5F8FF]/30 text-[#5B6472] hover:border-[#1B5AFF]/50'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 4 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 animate-fade-in text-center" data-testid="employee-onboarding-step-4">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-[#0A0F1E] mb-2">Your VETT profile is live.</h2>
            <p className="text-[#5B6472] mb-8">Start building your verified work history.</p>

            {/* Summary */}
            <div className="bg-[#F5F8FF]/50 rounded-xl p-4 mb-8 text-left">
              <h3 className="text-sm font-medium text-[#5B6472] mb-3">Profile Summary</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-[#1B5AFF]" />
                  <span className="text-[#0A0F1E]">{firstName} {lastName}</span>
                </div>
                {jobTitle && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-4 h-4 text-[#5B6472]" />
                    <span className="text-[#5B6472]">{jobTitle}</span>
                  </div>
                )}
                {location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#5B6472]" />
                    <span className="text-[#5B6472]">{location}</span>
                  </div>
                )}
                {skills.length > 0 && (
                  <div className="flex items-start gap-3">
                    <GraduationCap className="w-4 h-4 text-[#5B6472] mt-0.5" />
                    <span className="text-[#5B6472]">{skills.slice(0, 3).join(', ')}{skills.length > 3 ? ` +${skills.length - 3} more` : ''}</span>
                  </div>
                )}
                {opportunityStatus && opportunityStatus !== 'not_looking' && (
                  <div className="flex items-center gap-3 mt-2">
                    <div className={`w-2 h-2 rounded-full ${opportunityStatus === 'open' ? 'bg-green-500' : 'bg-amber-500'}`} />
                    <span className={opportunityStatus === 'open' ? 'text-green-400 text-sm' : 'text-amber-400 text-sm'}>
                      {opportunityStatus === 'open' ? 'Open to opportunities' : 'Casually looking'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => { handleComplete().then(() => navigate('/employee/profile')); }}
                disabled={loading}
                className="flex-1 bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl py-6 btn-glow"
                data-testid="view-profile-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                View My Profile
              </Button>
              <Button
                onClick={() => { handleComplete().then(() => navigate('/employee/records')); }}
                disabled={loading}
                variant="outline"
                className="flex-1 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl py-6"
                data-testid="request-verification-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Request a Verification
              </Button>
            </div>
          </Card>
        )}

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <div className="flex justify-between mt-6">
            <Button
              onClick={handleBack}
              disabled={currentStep === 1}
              variant="outline"
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="next-step-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeOnboarding;
